/**
 * السايدبار — 248px، بيطوي لـ68px. 13 عنصر في 4 مجموعات.
 * الماركب والقيم منسوخين من design/Main.dc.html (نفس السايدبار في كل الشاشات،
 * اللي بيتغيّر هو `.active` بس).
 */
import { Icon } from "./Icon";
import { NAV_GROUPS } from "./nav-items";
import { Fragment } from "react";
import { fmtNum } from "@/lib/format";
import { useRouter } from "@/lib/router";

export type ConnHealth = "ok" | "warn" | "down" | "unknown";

export type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  /** عدّاد أحمر جنب عنصر التنقّل (مفتاح العنصر ⇒ العدد) */
  counts?: Partial<Record<string, number>>;
  /** نطاق المستخدم — «بتشوف أقسامك بس» */
  scopeLabel?: string;
  health?: { telegram: ConnHealth; leadForm: ConnHealth };
};

/** بيتحقن وقت البناء من وحدة النشر */
const RAW_SHA = import.meta.env.VITE_BUILD_SHA ?? "";
// "unknown" هي قيمة الـDockerfile الافتراضية — ما تتعرضش كأنها رقم نسخة
const BUILD_TAG = RAW_SHA && RAW_SHA !== "unknown" ? RAW_SHA.slice(0, 7) : "";

const HEALTH_TEXT: Record<ConnHealth, { dot: string; suffix: string }> = {
  ok: { dot: "dot-ok", suffix: "شغّالة" },
  warn: { dot: "dot-warn", suffix: "فيها تأخير" },
  down: { dot: "dot-danger", suffix: "واقفة" },
  // ⚠️ ممنوع نقطة خضرا على حاجة ما اتفحصتش — ده شكل الفشل الصامت
  unknown: { dot: "dot-unknown", suffix: "لسه بنتأكد" },
};

export function Sidebar({
  collapsed,
  onToggle,
  counts = {},
  scopeLabel = "بتشوف أقسامك بس",
  health = { telegram: "unknown", leadForm: "unknown" },
}: SidebarProps) {
  const { path, navigate } = useRouter();
  const telegram = HEALTH_TEXT[health.telegram];
  const leadForm = HEALTH_TEXT[health.leadForm];

  return (
    <aside className={`side${collapsed ? " mini" : ""}`}>
      <div className="brand">
        <div className="brand-mark">
          <Icon name="book" size={20} stroke="#0F1D33" />
        </div>
        <div>
          <div className="brand-name">رتّل</div>
          <div className="brand-sub">السيستم الإداري</div>
        </div>
        <button
          type="button"
          className="side-toggle"
          onClick={onToggle}
          aria-label={collapsed ? "فتح القائمة" : "طيّ القائمة"}
          title={collapsed ? "فتح القائمة" : "طيّ القائمة"}
        >
          <Icon name="chevron" size={15} stroke="#667085" />
        </button>
      </div>

      <nav className="nav" aria-label="التنقّل الرئيسي">
        {NAV_GROUPS.map((group, gi) => (
          <Fragment key={group.label ?? `g${gi}`}>
            {group.label ? <div className="nav-label">{group.label}</div> : null}
            {group.items.map((item) => {
              const active = path === item.path || path.startsWith(`${item.path}/`);
              const count = counts[item.key];
              return (
                /* ⚠️ `title` بلا شرط: مالوش أي أثر بصري لما الاسم ظاهر،
                   وبيبقى الشبكة الوحيدة لما يختفي. ربطه بـ`collapsed` خلّاه
                   يضيع بالظبط في الحالة اللي محتاجينه فيها — الـmedia query
                   كانت بتطوي القائمة وحالة React بتفضل `false`.
                   ⛔ ومش كافي لوحده على اللمس (`title` مالهوش تحويم) — عشان
                   كده زرار الفتح رجع في `shell.css` كمان. */
                <button
                  key={item.key}
                  type="button"
                  className={`nav-item${active ? " active" : ""}`}
                  data-key={item.key}
                  aria-current={active ? "page" : undefined}
                  title={item.label}
                  onClick={() => navigate(item.path)}
                >
                  <Icon name={item.icon} size={17} stroke="#475569" />
                  {item.label}
                  {count ? <span className="nav-count num">{fmtNum(count)}</span> : null}
                </button>
              );
            })}
          </Fragment>
        ))}
      </nav>

      <div className="side-foot">
        <div className="conn" title={`إشعارات تليجرام ${telegram.suffix}`}>
          <span className={telegram.dot} />
          <span>إشعارات تليجرام {telegram.suffix}</span>
        </div>
        <div className="conn" title={`فورم الموقع ${leadForm.suffix === "شغّالة" ? "متّصل" : leadForm.suffix}`}>
          <span className={leadForm.dot} />
          <span>فورم الموقع {leadForm.suffix === "شغّالة" ? "متّصل" : leadForm.suffix}</span>
        </div>
        <div className="conn" title={scopeLabel}>
          <span className="dot-ok" />
          <span>{scopeLabel}</span>
        </div>
        {BUILD_TAG ? (
          // بصمة النسخة — عشان أي بلاغ من الفريق ييجي معاه رقم نسخته
          <div className="build-tag t-meta" title={`نسخة الواجهة ${BUILD_TAG}`}>
            <bdi dir="ltr">{BUILD_TAG}</bdi>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
